import { useRef, useState, type Ref, type RefObject } from 'react'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import type { DateValidationError } from '@mui/x-date-pickers/models'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Calendar } from '@/assets/icons'
import { getDatePickerSlotProps } from '@/components/date-picker/date-picker-styles'
import { parseFilterDate } from '../date-filter'

export default function TableDateInput({
  value,
  onChange,
  inputRef,
  label,
  containerRef,
  onOpenChange,
  error,
  errorId,
  onBlur,
  onValidationError,
}: {
  value: string
  onChange: (value: string) => void
  inputRef: Ref<HTMLInputElement>
  label: string
  containerRef: RefObject<HTMLFormElement>
  onOpenChange: (open: boolean) => void
  error: boolean
  errorId: string
  onBlur: () => void
  onValidationError: (error: DateValidationError) => void
}) {
  const calendarOpen = useRef(false)
  const [entry, setEntry] = useState(() => ({ value, date: parseFilterDate(value) }))
  if (entry.value !== value) setEntry({ value, date: parseFilterDate(value) })
  const styles = getDatePickerSlotProps(error, { compact: true })
  return (
    <div
      onBlur={(event) => {
        if (!calendarOpen.current && !event.currentTarget.contains(event.relatedTarget)) onBlur()
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          value={entry.date}
          onChange={(date, context) => {
            const next = date && !context.validationError ? date.format('YYYY-MM-DD') : date ? 'Invalid Date' : ''
            // Preserve MUI's invalid date instance so incomplete sections remain editable.
            setEntry({ value: next, date })
            onChange(next)
          }}
          onError={onValidationError}
          inputRef={inputRef}
          format="MM/DD/YYYY"
          views={['year', 'month', 'day']}
          openTo="day"
          closeOnSelect
          onOpen={() => {
            calendarOpen.current = true
            onOpenChange(true)
          }}
          onClose={() => {
            calendarOpen.current = false
            onOpenChange(false)
            onBlur()
          }}
          slots={{ openPickerIcon: () => <Calendar aria-hidden="true" className="h-4 w-4" /> }}
          slotProps={{
            ...styles,
            textField: {
              ...styles.textField,
              InputProps: { 'aria-label': label, 'aria-labelledby': undefined, 'aria-describedby': error ? errorId : undefined },
              error,
            },
            openPickerButton: { 'aria-label': `Choose date for ${label.toLowerCase()}`, edge: false },
            popper: { ...styles.popper, 'aria-label': `Choose date for ${label.toLowerCase()}`, container: () => containerRef.current },
          }}
        />
      </LocalizationProvider>
    </div>
  )
}
